/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { EditorContent } from '@tiptap/react';
import cx from 'classnames';
import type { FunctionComponent } from 'react';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { RicosContext, useTrackRender } from 'ricos-context';
import { getLangDir } from 'wix-rich-content-common';
import { RICOS_TIPTAP_EDITOR_DATA_HOOK } from 'wix-rich-content-editor-common';

import { RicosTiptapContext } from '../../context';
import styles from '../../statics/styles/tiptap-editor-styles.scss';
import type { RicosTiptapEditorProps } from '../../types';
import { focusReducer } from './focus-reducer';
import { useEditorSideEffects } from './use-editor-side-effects';
import { useJsdomWidthFix } from './use-jsdom-width-fix';
import { useKeyboardNavigationTooltip } from './use-keyboard-navigation-tooltip';
import { useKeydownListener } from './use-keydown-listener';
import { useUserInteraction } from './use-user-interaction';

export const RicosTiptapEditor: FunctionComponent<RicosTiptapEditorProps> = props => {
  const { editor, onUpdate, onSelectionUpdate, editorStyleClasses, htmlAttributes, ...context } =
    props;
  const { localeContent } = useContext(RicosContext);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [state, dispatch] = useReducer(focusReducer, 'not-focused');
  const { lastInteraction } = useUserInteraction();
  const tooltip = useKeyboardNavigationTooltip();

  useTrackRender('RicosTiptapEditor');
  useJsdomWidthFix(ref, editor);
  useEditorSideEffects({ editor, onUpdate, onSelectionUpdate });

  const handleKeyDown = (event: KeyboardEvent) => {
    dispatch({ type: 'keydown', event });
    tooltip.closeTooltip();
  };

  const handleMouseDown = () => {
    dispatch({ type: 'pointer-focus' });
    tooltip.closeTooltip();
  };

  useKeydownListener(ref, handleKeyDown);

  useEffect(() => {
    if (state === 'editor-focused-outlined') {
      tooltip.openTooltip(ref);
    }
  }, [state]);

  return (
    <RicosTiptapContext.Provider
      value={{
        context: {
          ...context,
        },
      }}
    >
      <div
        data-hook={RICOS_TIPTAP_EDITOR_DATA_HOOK}
        dir={getLangDir(localeContent)}
        className={cx(editorStyleClasses?.containerClassName, styles.ricosTiptapEditor, {
          [styles.focusRing]: state === 'editor-focused-outlined' || state === 'container-focused',
        })}
        style={state === 'container-focused' ? { caretColor: 'transparent' } : {}}
        onMouseDown={handleMouseDown}
        onBlur={() => {
          dispatch({ type: 'blur' });
        }}
        onFocus={() => {
          dispatch({
            type: lastInteraction === 'keyboard' ? 'keyboard-focus' : 'pointer-focus',
          });
        }}
        {...htmlAttributes}
        tabIndex={-1}
        ref={setRef}
      >
        <EditorContent editor={editor} className={editorStyleClasses?.editorClassName} />
        {props.devToolsElement}
      </div>
    </RicosTiptapContext.Provider>
  );
};
